import React from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  SelectChangeEvent,
  TextFieldProps,
  FormControlProps,
  SelectProps,
} from "@mui/material";

export interface FormFieldProps {
  name: string;
  label: string;
  value: any;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectChange?: (event: SelectChangeEvent) => void;
  error?: string;
  type?: "text" | "email" | "password" | "number" | "date" | "textarea";
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  multiline?: boolean;
  rows?: number;
  select?: boolean;
  options?: Array<{value: string | number; label: string}>;
  inputProps?: TextFieldProps["inputProps"];
  helperText?: string;
  margin?: "none" | "dense" | "normal";
  size?: "small" | "medium";
  autoFocus?: boolean;
  autoComplete?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
}

const FormField: React.FC<FormFieldProps> = ({
  name,
  label,
  value,
  onChange,
  onSelectChange,
  error,
  type = "text",
  required = false,
  disabled = false,
  fullWidth = true,
  multiline = false,
  rows = 1,
  select = false,
  options = [],
  inputProps = {},
  helperText,
  margin = "normal",
  size = "medium",
  autoFocus = false,
  autoComplete,
  placeholder,
  min,
  max,
  step,
}) => {
  // Handle number input props
  const numberInputProps =
    type === "number"
      ? {
          min,
          max,
          step,
          ...inputProps,
        }
      : inputProps;

  // Handle date input props
  const dateInputProps =
    type === "date"
      ? {
          InputLabelProps: {
            shrink: true,
          },
          ...inputProps,
        }
      : inputProps;

  if (select) {
    return (
      <FormControl fullWidth={fullWidth} margin={margin} size={size} error={!!error} disabled={disabled} required={required}>
        <InputLabel id={`${name}-label`}>{label}</InputLabel>
        <Select
          labelId={`${name}-label`}
          id={name}
          name={name}
          value={value || ""}
          label={label}
          onChange={onSelectChange || (() => {})}
          autoFocus={autoFocus}>
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {(error || helperText) && <FormHelperText error={!!error}>{error || helperText}</FormHelperText>}
      </FormControl>
    );
  }

  return (
    <TextField
      fullWidth={fullWidth}
      margin={margin}
      size={size}
      id={name}
      name={name}
      label={label}
      type={type}
      value={value || ""}
      onChange={onChange}
      error={!!error}
      helperText={error || helperText}
      required={required}
      disabled={disabled}
      multiline={multiline}
      rows={multiline ? rows : undefined}
      inputProps={type === "number" ? numberInputProps : inputProps}
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      placeholder={placeholder}
      {...(type === "date" ? {InputLabelProps: {shrink: true}} : {})}
    />
  );
};

export default FormField;
